<template>
  <div v-if="isVisible">
    <div v-if="showFieldTitle" class="field__inner-title mb-3">
      <div>{{ schema.title }}</div>
      <p v-if="schema.help">{{ schema.help }}</p>
    </div>

    <v-menu
      v-model="datePickerModal"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      min-width="290"
    >
      <template v-slot:activator="{ on }">
        <div v-on="isDisabled ? {} : on">
          <v-text-field
            :value="textValue"
            :readonly="!dateTextEditable"
            :disabled="isDisabled"
            :label="fieldLabel"
            :placeholder="fieldPlaceholder"
            :class="fieldClasses"
            :rules="rules"
            :clearable="!isDisabled"
            :inputmode="usesUsDateMask ? formatInputProps.inputmode : undefined"
            persistent-hint
            v-mask="dateMask"
            @input="onTextInput"
            @focus="onFocus"
            @blur="onTextBlur"
            @keydown.enter.native="onTextEnter"
            @keydown.esc.native="onTextEscape"
          />
        </div>
      </template>

      <v-date-picker
        :value="pickerValue"
        color="primary"
        no-title
        :min="schema.min"
        :max="schema.max"
        @input="onPickerInput"
      />
    </v-menu>
  </div>
</template>

<script>
import GenericField from './GenericField';
import { mask } from 'vue-the-mask';
import { format, fromUnixTime, parseISO, getUnixTime } from 'date-fns';
import { displayFormat, validateFormat } from '@/lib/fieldFormats';

const ISO_DATE_FORMAT = 'yyyy-MM-dd';
const US_DATE_MDY = 'us_date_mdy';

export default {
  name: 'DateField',
  mixins: [GenericField],
  directives: {
    mask,
  },
  data() {
    return {
      datePickerModal: false,
      draftValue: null,
      lastCommittedDraft: null,
    };
  },
  watch: {
    value() {
      this.draftValue = null;
      this.lastCommittedDraft = null;
    },
  },
  computed: {
    usesUsDateMask() {
      return this.resolvedFormat === US_DATE_MDY;
    },

    dateMask() {
      if (this.usesUsDateMask) {
        return this.formatInputProps.mask || '##/##/####';
      }
      return '####-##-##';
    },

    datePlaceholder() {
      if (this.usesUsDateMask) {
        return this.formatInputProps.placeholder || 'MM/DD/YYYY';
      }
      return 'YYYY-MM-DD';
    },

    showFieldTitle() {
      if (!this.showTitle) {
        return false;
      }

      const { placeholder, title } = this.schema || {};

      // When no explicit placeholder, title is shown inside the input instead
      if (!placeholder && title) {
        return false;
      }

      return true;
    },

    dateTextEditable() {
      return !!(this.schema?.dateEditable || this.usesUsDateMask);
    },

    fieldPlaceholder() {
      const { hideLabel, placeholder, label, title } = this.schema || {};

      if (placeholder) {
        return placeholder;
      }

      if (hideLabel) {
        return label || title || this.datePlaceholder;
      }

      if (this.dateTextEditable) {
        return this.datePlaceholder;
      }

      return '';
    },

    fieldLabel() {
      if (this.showFieldTitle) {
        return '';
      }

      const { label, title } = this.schema || {};

      return label || title || 'Date';
    },

    formattedDisplay() {
      if (!this.value) return null;

      if (this.usesUsDateMask) {
        const shown = displayFormat(US_DATE_MDY, this.value, {
          context: 'edit',
        });
        return shown || null;
      }

      if (this.isTimestamp(this.value)) {
        const seconds =
          this.value > 9999999999 ? Math.floor(this.value / 1000) : this.value;

        return format(fromUnixTime(seconds), ISO_DATE_FORMAT);
      }

      return this.value;
    },

    textValue() {
      if (this.draftValue !== null) return this.draftValue;
      return this.formattedDisplay;
    },

    pickerValue() {
      if (!this.usesUsDateMask) return this.formattedDisplay;
      if (!this.value) return null;

      const parsed = validateFormat(US_DATE_MDY, this.value, false);
      return parsed.valid ? parsed.value : null;
    },
  },
  methods: {
    isTimestamp(value) {
      const num = Number(value);
      return !isNaN(num) && num > 946684800;
    },

    onFocus() {
      // Focus alone must not emit or clear the stored value.
    },

    onTextInput(val) {
      if (!this.usesUsDateMask) {
        this.commitValue(val);
        return;
      }

      this.datePickerModal = false;
      const next = val == null ? '' : String(val);
      const saved = this.formattedDisplay || '';
      if (next === saved) {
        this.draftValue = null;
        return;
      }

      this.draftValue = next;
    },

    onTextBlur() {
      if (!this.usesUsDateMask) return;
      this.commitUsDraft();
    },

    onTextEnter(event) {
      if (!this.usesUsDateMask) return;
      if (event && event.preventDefault) event.preventDefault();
      this.commitUsDraft();
    },

    onTextEscape() {
      if (!this.usesUsDateMask) {
        this.datePickerModal = false;
        return;
      }

      this.draftValue = null;
      this.lastCommittedDraft = null;
      this.datePickerModal = false;
    },

    commitUsDraft() {
      if (this.draftValue == null) return;

      const draft = this.draftValue;
      if (draft === '') {
        if (this.lastCommittedDraft === '') return;
        this.lastCommittedDraft = '';
        this.commitValue('');
        return;
      }

      const parsed = validateFormat(US_DATE_MDY, draft, false);
      if (!parsed.valid) return;

      if (
        parsed.value === this.pickerValue ||
        parsed.value === this.lastCommittedDraft
      ) {
        this.draftValue = null;
        return;
      }

      this.lastCommittedDraft = parsed.value;
      this.commitValue(parsed.value);
    },

    onPickerInput(val) {
      if (this.usesUsDateMask && val) {
        this.draftValue = displayFormat(US_DATE_MDY, val, { context: 'edit' });
      } else {
        this.draftValue = null;
      }
      this.commitValue(val);
    },

    commitValue(val) {
      this.datePickerModal = false;
      this.emitValue(this.normalizeOutput(val));
    },

    normalizeOutput(value) {
      if (!value) return value;

      if (this.isTimestamp(this.value)) {
        return getUnixTime(parseISO(value));
      }

      return value;
    },

    emitValue(value) {
      this.item ? this.save(value) : this.$emit('input', value);
    },
  },
};
</script>
